import React, { useState, useEffect } from 'react';
import { Button, Flex, Panel } from '@maxhub/max-ui';
import { Event, Guest } from '../types';
import { api } from '../api/client';

interface EventCardProps {
  event: Event;
  onRegister: (eventId: number, guestName?: string, existingGuestId?: number) => void;
}

export const EventCard: React.FC<EventCardProps> = ({ event, onRegister }) => {
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [useExistingGuest, setUseExistingGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [existingGuestId, setExistingGuestId] = useState<number | null>(null);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loadingGuests, setLoadingGuests] = useState(false);

  const date = new Date(event.datetime);
  const formattedDate = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const formattedTime = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const isRegistered = event.user_registered;
  const isWaitlisted = event.user_waitlisted || false;
  const isFull = event.available_slots === 0;
  const hasGuest = event.user_has_guest || false;

  // ============================================================
  //  ПОКАЗ ЧЕКБОКСА "ЗАПИСАТЬ ДРУГОГО ЧЕЛОВЕКА"
  //  Показываем, если клиент ещё не записал гостя на это мероприятие
  // ============================================================
  const showGuestOption = !hasGuest;

  useEffect(() => {
    if (isGuestMode && guests.length === 0 && !loadingGuests) {
      loadGuests();
    }
    if (!isGuestMode) {
      setUseExistingGuest(false);
      setGuestName('');
      setExistingGuestId(null);
    }
  }, [isGuestMode]);

  const loadGuests = async () => {
    setLoadingGuests(true);
    try {
      const response = await api.get('/my-guests');
      setGuests(response.data);
    } catch (error) {
      console.error('Ошибка загрузки гостей:', error);
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleRegisterClick = () => {
    console.log('🔍 handleRegisterClick вызван');
    console.log('🔍 isGuestMode:', isGuestMode);
    console.log('🔍 useExistingGuest:', useExistingGuest);
    console.log('🔍 guestName:', guestName);
    console.log('🔍 existingGuestId:', existingGuestId);

    if (isGuestMode) {
      if (useExistingGuest) {
        if (!existingGuestId) {
          alert('Выберите гостя из списка');
          return;
        }
        console.log('📤 Передаём existingGuestId:', existingGuestId);
        onRegister(event.id, undefined, existingGuestId);
      } else {
        if (!guestName.trim()) {
          alert('Введите имя гостя');
          return;
        }
        console.log('📤 Передаём guestName:', guestName.trim());
        onRegister(event.id, guestName.trim());
      }
    } else {
      console.log('📤 Запись на себя (без гостя)');
      onRegister(event.id);
    }
  };

  const isGuestDataValid = useExistingGuest
    ? !!existingGuestId
    : guestName.trim().length > 0;

  // ============================================================
  //  ЛОГИКА ДОСТУПНОСТИ КНОПКИ
  // ============================================================
  //  Кнопка заблокирована, если:
  //  1. Включён режим гостя, но данные гостя невалидны
  //  2. Пользователь в листе ожидания и НЕ пытается записать гостя
  //     (записать себя дважды в лист ожидания нельзя)
  // ============================================================
  const isButtonDisabled =
    (isGuestMode && !isGuestDataValid) ||
    (!isGuestMode && isWaitlisted);

  // ============================================================
  //  ТЕКСТ КНОПКИ
  // ============================================================
  let buttonText = '✅ Записаться';

  if (isGuestMode) {
    // Режим записи гостя
    if (isFull) {
      buttonText = '⏳ В лист ожидания (гость)';
    } else {
      buttonText = '✅ Записать гостя';
    }
  } else if (isRegistered) {
    // Пользователь записан на себя
    buttonText = hasGuest ? '✅ Вы и гость записаны' : '✅ Вы записаны';
  } else if (isWaitlisted) {
    // Пользователь в листе ожидания
    buttonText = '⏳ Вы в листе ожидания';
  } else if (isFull) {
    // Мест нет, пользователь не записан
    buttonText = '⏳ В лист ожидания';
  }

  return (
    <Panel style={{
      marginBottom: 16,
      padding: 16,
      backgroundColor: '#fff',
      borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }}>
      <h3 style={{
        fontSize: 18,
        fontWeight: 'bold',
        margin: '0 0 4px 0',
        color: '#1a1a1a'
      }}>
        {event.title}
        {isRegistered && (
          <span style={{
            fontSize: 14,
            color: '#4CAF50',
            marginLeft: 8,
            fontWeight: 'normal'
          }}>
            ✅ Записаны
          </span>
        )}
        {!isRegistered && isWaitlisted && (
          <span style={{
            fontSize: 14,
            color: '#ff9800',
            marginLeft: 8,
            fontWeight: 'normal'
          }}>
            ⏳ В листе ожидания
          </span>
        )}
        {hasGuest && (
          <span style={{
            fontSize: 14,
            color: '#4CAF50',
            marginLeft: 8,
            fontWeight: 'normal'
          }}>
            ✅ Гость записан
          </span>
        )}
      </h3>

      <p style={{
        fontSize: 14,
        color: '#555',
        margin: '4px 0 8px 0'
      }}>
        📅 {formattedDate} в {formattedTime}
      </p>

      <p style={{
        fontSize: 14,
        color: '#333',
        margin: '0 0 12px 0',
        whiteSpace: 'pre-wrap',
      }}>
        {event.description}
      </p>

      <p style={{
        fontSize: 14,
        color: '#555',
        margin: '4px 0 8px 0'
      }}>
        📍 {event.location || 'Место не указано'}
      </p>

      <Flex gap={8} style={{ marginTop: 12 }}>
        <span style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 16,
          border: '1px solid #ddd',
          fontSize: 14,
          color: '#1a1a1a'
        }}>
          💰 {event.price} ₽
        </span>
        <span style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 16,
          backgroundColor: '#f0f0f0',
          fontSize: 14,
          color: '#1a1a1a'
        }}>
          👥 {event.available_slots} / {event.max_participants}
        </span>
      </Flex>

      {/* Чекбокс для записи гостя */}
      {showGuestOption && (
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isGuestMode}
              onChange={(e) => {
                setIsGuestMode(e.target.checked);
                if (!e.target.checked) {
                  setUseExistingGuest(false);
                  setGuestName('');
                  setExistingGuestId(null);
                }
              }}
            />
            Записать другого человека
          </label>

          {isGuestMode && (
            <div style={{ marginTop: 8, paddingLeft: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={useExistingGuest}
                  onChange={(e) => {
                    setUseExistingGuest(e.target.checked);
                    if (e.target.checked) {
                      setGuestName('');
                    } else {
                      setExistingGuestId(null);
                    }
                  }}
                />
                Выбрать из ранее записанных
              </label>

              {useExistingGuest ? (
                <div style={{ marginTop: 4 }}>
                  {loadingGuests ? (
                    <span style={{ fontSize: 14, color: '#666' }}>Загрузка...</span>
                  ) : guests.length === 0 ? (
                    <span style={{ fontSize: 14, color: '#999' }}>
                      Нет ранее записанных гостей. Введите имя вручную.
                    </span>
                  ) : (
                    <select
                      value={existingGuestId || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setExistingGuestId(val ? Number(val) : null);
                        console.log('🔍 Выбран гость ID:', val);
                      }}
                      style={{
                        width: '100%',
                        padding: 6,
                        borderRadius: 4,
                        border: '1px solid #ddd',
                        fontSize: 14,
                      }}
                    >
                      <option value="">-- Выберите гостя --</option>
                      {guests.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.full_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 4 }}>
                  <input
                    type="text"
                    placeholder="Имя гостя *"
                    value={guestName}
                    onChange={(e) => {
                      setGuestName(e.target.value);
                      console.log('🔍 Введено имя гостя:', e.target.value);
                    }}
                    style={{
                      width: '100%',
                      padding: 6,
                      borderRadius: 4,
                      border: '1px solid #ddd',
                      fontSize: 14,
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Flex gap={8} style={{ marginTop: 12 }}>
        <Button
          style={{
            backgroundColor: isButtonDisabled ? '#ccc' : (isFull ? '#ff9800' : '#4CAF50'),
            color: '#fff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 8,
            cursor: isButtonDisabled ? 'default' : 'pointer',
            opacity: 1,
          }}
          onClick={handleRegisterClick}
          disabled={isButtonDisabled}
        >
          {buttonText}
        </Button>
      </Flex>
    </Panel>
  );
};